import { useEffect, useRef, useState } from 'react'
import { Download, Link2, Lock, Pencil, Trash2 } from 'lucide-react'

import { Button, ExplainDisabled } from '@/components/ui/button'
import { Badge, ErrorNote, Spinner } from '@/components/ui/misc'
import { Drawer } from '@/components/ui/overlay'
import { api, ApiError, type ObjectMeta } from '@/lib/api'
import { formatBytes, formatDateTime } from '@/lib/format'
import { useAuth } from '@/features/auth/AuthProvider'
import { ShareDialog } from '@/features/shares/ShareDialog'
import { PublicLinkPanel } from './PublicLinkPanel'
import { RenameDialog } from './RenameDialog'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="col-span-2 break-words text-sm">{value}</dd>
    </div>
  )
}

export function DetailDrawer({
  bucket,
  objectKey,
  onClose,
  onDelete,
  onMetaLoaded,
  onRenamed,
}: {
  bucket: string
  objectKey: string | null
  onClose: () => void
  onDelete: (key: string) => void
  onMetaLoaded: (meta: ObjectMeta) => void
  onRenamed: (newKey: string) => void
}) {
  const { canEdit } = useAuth()
  const [meta, setMeta] = useState<ObjectMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)
  const [renaming, setRenaming] = useState(false)

  // Held in a ref rather than listed as a dependency. The callback notifies the
  // parent, which updates state the parent may pass back down; depending on its
  // identity would let a caller that recreates it each render drive this effect
  // in an unbounded loop, firing one HEAD request per iteration.
  const onMetaLoadedRef = useRef(onMetaLoaded)
  onMetaLoadedRef.current = onMetaLoaded

  useEffect(() => {
    if (!objectKey) {
      setMeta(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    api
      .meta(bucket, objectKey)
      .then((res) => {
        if (cancelled) return
        setMeta(res.meta)
        // Shared back so the table's lock badge stays consistent with what the
        // drawer is showing.
        onMetaLoadedRef.current(res.meta)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load object details')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [bucket, objectKey])

  const deleteDisabledReason = !canEdit
    ? 'Deleting requires the Editor role.'
    : meta?.locked
      ? `${meta.lockReason}. HCP will not delete an object that is under retention or hold.`
      : null

  return (
    <Drawer
      open={objectKey !== null}
      onClose={onClose}
      title={objectKey?.split('/').pop() ?? 'Object'}
      footer={
        meta && (
          <>
            <ExplainDisabled
              reason={
                !canEdit
                  ? 'Renaming requires the Editor role.'
                  : meta.locked
                    ? `${meta.lockReason}. Renaming requires deleting the original, which HCP will not allow.`
                    : null
              }
            >
              <Button
                variant="ghost"
                disabled={!canEdit || meta.locked}
                onClick={() => setRenaming(true)}
              >
                <Pencil className="h-4 w-4" />
                Rename
              </Button>
            </ExplainDisabled>
            <Button variant="ghost" onClick={() => setSharing(true)}>
              <Link2 className="h-4 w-4" />
              Share
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.open(api.downloadUrl(bucket, meta.key), '_blank')}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
            <ExplainDisabled reason={deleteDisabledReason}>
              <Button
                variant="danger"
                disabled={Boolean(deleteDisabledReason)}
                onClick={() => onDelete(meta.key)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </ExplainDisabled>
          </>
        )
      }
    >
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner className="h-5 w-5" />
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {meta && (
        <div className="space-y-4">
          {meta.locked && (
            <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/10 px-3 py-2.5">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
              <div className="space-y-0.5 text-sm">
                <p className="font-medium text-danger">{meta.lockReason}</p>
                <p className="text-muted-foreground">
                  This object is protected by HCP and cannot be deleted or replaced until the
                  protection ends.
                </p>
              </div>
            </div>
          )}

          <dl className="divide-y divide-border">
            <Row label="Key" value={<span className="font-mono text-xs">{meta.key}</span>} />
            <Row label="Size" value={`${formatBytes(meta.size)} (${meta.size.toLocaleString()} bytes)`} />
            <Row label="Modified" value={formatDateTime(meta.lastModified)} />
            <Row label="Content type" value={meta.contentType || '—'} />
            <Row label="ETag" value={<span className="font-mono text-xs">{meta.etag}</span>} />
            <Row
              label="Retention"
              value={
                meta.retentionUntil ? (
                  <span>
                    Until {formatDateTime(meta.retentionUntil)}{' '}
                    {!meta.locked && <Badge tone="success">expired</Badge>}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Not set</span>
                )
              }
            />
            <Row
              label="Legal hold"
              value={
                meta.retentionHold ? (
                  <Badge tone="danger">
                    <Lock className="h-3 w-3" />
                    On hold
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">None</span>
                )
              }
            />
          </dl>

          <PublicLinkPanel bucket={bucket} objectKey={meta.key} />
        </div>
      )}

      {sharing && meta && (
        <ShareDialog bucket={bucket} objectKey={meta.key} onClose={() => setSharing(false)} />
      )}

      {renaming && meta && (
        <RenameDialog
          bucket={bucket}
          objectKey={meta.key}
          isLocked={meta.locked}
          lockReason={meta.lockReason}
          onClose={() => setRenaming(false)}
          onRenamed={onRenamed}
        />
      )}
    </Drawer>
  )
}
